import type { PlayerColor } from '@/shared/board/types'
import type { TokenPosition } from '@/shared/game/types'
import { cornerIndexes, HOME_LENGTH, LAP_LENGTH, QUARTER, RING_LENGTH, startIndex } from './board'

/**
 * Путь фишки — порт App\Domain\Ludo\Track.
 *
 * Board знает форму доски, Track — как по ней движутся: где начинается
 * отсчёт каждого цвета, когда фишка сворачивает в дом и что считается
 * перелётом. Правила ходов (кто кого рубит, что мешает) лежат выше.
 */

export const base = (): TokenPosition => ({ location: 'base', index: 0 })
export const ring = (index: number): TokenPosition => ({ location: 'ring', index })
export const home = (index: number): TokenPosition => ({ location: 'home', index })

export function samePosition(one: TokenPosition, other: TokenPosition): boolean {
  return one.location === other.location && one.index === other.index
}

/**
 * Сколько клеток фишка уже прошла, если стоит на клетке кольца.
 *
 * Пройденный путь в самой фишке не хранится: мимо своего входа в дом она не
 * проходит, поэтому позиция задаёт пройденное однозначно.
 */
export function progress(color: PlayerColor, index: number): number {
  return (index - startIndex(color) + RING_LENGTH) % RING_LENGTH
}

/**
 * Клетки, которые фишка пройдёт, сделав steps шагов, — включая конечную,
 * не включая текущую.
 *
 * null — ход невозможен: перелёт через дом недопустим, а из двора обычным
 * ходом не выходят.
 */
export function path(
  color: PlayerColor,
  from: TokenPosition,
  steps: number,
): TokenPosition[] | null {
  if (steps < 1 || from.location === 'base') return null

  const cells: TokenPosition[] = []

  if (from.location === 'home') {
    for (let step = 1; step <= steps; step++) {
      const index = from.index + step

      if (index >= HOME_LENGTH) return null

      cells.push(home(index))
    }

    return cells
  }

  const passed = progress(color, from.index)

  for (let step = 1; step <= steps; step++) {
    const distance = passed + step

    if (distance <= LAP_LENGTH) {
      cells.push(ring((startIndex(color) + distance) % RING_LENGTH))

      continue
    }

    // Круг пройден — дальше домашняя дорожка. Перелетать её нельзя:
    // недоход разрешён, перелёт нет.
    const index = distance - LAP_LENGTH - 1

    if (index >= HOME_LENGTH) return null

    cells.push(home(index))
  }

  return cells
}

/** Номер угла по порядку хода, если фишка стоит в углу. null — прыгать неоткуда. */
export function cornerAt(position: TokenPosition): number | null {
  if (position.location !== 'ring') return null

  const at = cornerIndexes().indexOf(position.index)

  return at === -1 ? null : at
}

/**
 * Углы, через которые пройдёт прыжок на corners углов вперёд, — включая
 * конечный. null, если прыжок уводит фишку за её вход в дом.
 */
export function jump(color: PlayerColor, from: TokenPosition, corners: number): number[] | null {
  const at = cornerAt(from)

  if (at === null || corners < 1) return null

  const all = cornerIndexes()

  if (corners >= all.length) return null

  // Прыжок — движение вперёд по кольцу на целую четверть за угол.
  if (progress(color, from.index) + corners * QUARTER > LAP_LENGTH) return null

  return Array.from({ length: corners }, (_, step) => all[(at + step + 1) % all.length])
}
